import { useState, useCallback } from 'react';

export function useMultiSelection<T>(
  allItems: T[],
  getKey: (item: T) => string | number
) {
  const [selectedKeys, setSelectedKeys] = useState<Set<string | number>>(new Set());
  const [lastClickedKey, setLastClickedKey] = useState<string | number | null>(null);

  const handleSelection = useCallback((item: T, index: number, e: React.MouseEvent) => {
    const key = getKey(item);

    if (e.shiftKey && lastClickedKey !== null) {
      document.getSelection()?.removeAllRanges();
      const start = allItems.findIndex((i) => getKey(i) === lastClickedKey);
      const end = index;
      const newSelected = new Set(selectedKeys);
      const low = Math.min(start, end);
      const high = Math.max(start, end);

      for (let i = low; i <= high; i += 1) {
        newSelected.add(getKey(allItems[i]));
      }
      setSelectedKeys(newSelected);
    } else if (e.ctrlKey || e.metaKey) {
      const newSelected = new Set(selectedKeys);
      if (newSelected.has(key)) {
        newSelected.delete(key);
      } else {
        newSelected.add(key);
        setLastClickedKey(key);
      }
      setSelectedKeys(newSelected);
    } else {
      setSelectedKeys(new Set([key]));
      setLastClickedKey(key);
    }
  }, [allItems, lastClickedKey, selectedKeys, getKey]);

  const clearSelection = useCallback(() => {
    setSelectedKeys(new Set());
    setLastClickedKey(null);
  }, []);

  return {
    selectedKeys,
    handleSelection,
    clearSelection,
    isSelected: (item: T) => selectedKeys.has(getKey(item)),
    getSelectedItems: () => allItems.filter((item) => selectedKeys.has(getKey(item))),
  };
}
